import { useEffect, useState } from 'preact/hooks';
import { api } from '../api/client';
import type { NotificationInbox } from '../api/types';
import { subscribeStream } from './eventStream';

/**
 * Shared notification-inbox state (ADR-0028 + ADR-0029). The bell and the alert
 * banner consume the same inbox payload, so a single module-level singleton
 * collapses their reads. Delivery is realtime-first: one shared SSE stream pushes
 * an `inbox` nudge when something the viewer can see changes, and we re-fetch the
 * server-scoped inbox in response. Polling is the FALLBACK - it runs only while
 * the stream is disconnected (older browsers, a dropped connection, or a capacity
 * rejection), so the chrome still updates even with SSE unavailable.
 *
 * Reference-counted: the stream + any fallback poll run only while at least one
 * component is subscribed (bell + banner mount inside the authenticated nav), and
 * stop when the last unmounts. A 401/error resolves to the empty inbox so the
 * chrome stays quiet rather than surfacing transport noise.
 */
export interface InboxState {
    status: 'loading' | 'ready' | 'error';
    inbox: NotificationInbox;
    /** Re-fetch now (e.g. after a mark-read / dismiss mutation). */
    refresh: () => void;
}

const POLL_MS = 45_000;
const EMPTY_INBOX: NotificationInbox = { items: [], unreadCount: 0, banners: [] };

type Snapshot = Omit<InboxState, 'refresh'>;
type Listener = (snapshot: Snapshot) => void;

let cached: Snapshot = { status: 'loading', inbox: EMPTY_INBOX };
let inflight: Promise<void> | null = null;
let timer: number | null = null;
let unsubscribeStream: (() => void) | null = null;
const listeners = new Set<Listener>();

function publish(next: Snapshot): void {
    cached = next;
    listeners.forEach((l) => l(next));
}

function poll(): Promise<void> {
    if (inflight) return inflight;
    inflight = api.notifications
        .getInbox()
        .then((inbox) => {
            publish({ status: 'ready', inbox });
        })
        .catch(() => {
            // 401 (anonymous) and transient errors collapse to a quiet,
            // empty inbox - the chrome never screams a transport blip.
            publish({ status: 'error', inbox: EMPTY_INBOX });
        })
        .finally(() => {
            inflight = null;
        });
    return inflight;
}

function startInterval(): void {
    if (timer !== null) return;
    timer = window.setInterval(() => void poll(), POLL_MS);
}

function stopInterval(): void {
    if (timer === null) return;
    window.clearInterval(timer);
    timer = null;
}

function onStreamConnected(streamConnected: boolean): void {
    if (streamConnected) {
        // SSE up: a fresh-on-connect catch-up read covers anything missed while
        // reconnecting, then nudges drive every later refresh - no polling.
        void poll();
        stopInterval();
    } else {
        // SSE down/unavailable: poll as the fallback so the inbox still updates.
        startInterval();
    }
}

function startSource(): void {
    if (unsubscribeStream) return;
    void poll(); // seed immediately on first mount
    unsubscribeStream = subscribeStream('inbox', () => void poll(), onStreamConnected);
}

function stopSource(): void {
    if (unsubscribeStream) {
        unsubscribeStream();
        unsubscribeStream = null;
    }
    stopInterval();
}

export function useInbox(): InboxState {
    const [snapshot, setSnapshot] = useState<Snapshot>(cached);

    useEffect(() => {
        listeners.add(setSnapshot);
        // First subscriber opens the shared stream (+ seeds the cache); later
        // mounts see the resolved snapshot immediately.
        if (listeners.size === 1) {
            startSource();
        } else if (snapshot !== cached) {
            setSnapshot(cached);
        }
        return () => {
            listeners.delete(setSnapshot);
            if (listeners.size === 0) stopSource();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { ...snapshot, refresh: () => void poll() };
}
